import React, { useState, useEffect, useMemo } from 'react';
import { Calendar } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSolicitudesDisponiblesModel } from '../../../../modelos/Solicitudes/modeloSolicitudes';
import useClientesModel from '../../../../modelos/Clientes/modeloClientes';
import { useDetalleSolicitudModel } from '../../../../modelos/Solicitudes/modeloSolicitud';
import { formatearFechaCorta, formatearPrecio } from '../../../../utils/utils';
import { useConfiguracion } from '../../../../metodos/ConfiguracionContext';
import { useContabilidadModel } from '../../../../modelos/Contabilidad/modeloContabilidad';
import { useNombreEmpresaReal } from '../../../../config/unificacion';
import SolicitudesProPlataforma from './Modulos/SolicitudesProPlataforma';
import HeaderProPlataforma from './Modulos/HeaderProPlataforma';
import ServiciosProPlataforma from './Modulos/ServiciosProPlataforma';
import { generarYVerificarPlanCompleto } from './Components/FuncionesProPlataforma';

const DetalleProPlataforma = () => {
    const {nombreEmpresa, numeroCliente } = useParams();
    const nombreEmpresaReal = useNombreEmpresaReal(nombreEmpresa);
    const navigate = useNavigate();
    const {
        solicitudesPorCliente,
        loadingSolicitudes,
        cargarSolicitudesDeCliente,
        setupWebSocketsSolicitudes,
    } = useSolicitudesDisponiblesModel();
    const {
        cliente,
        loadingClientes,
        errorClientes,
        getCliente,
        updateCliente,
        setupWebSocketsClientes,
    } = useClientesModel();
    
    const { configuracion} = useConfiguracion();
    const [semestreSeleccionado, setSemestreSeleccionado] = useState('');
    const [seccionActiva, setSeccionActiva] = useState('solicitudes');

    //obtener servicios agendados
    const {
        loadingServicios,
        getServiciosPorCliente,
        serviciosPorClientes,
        setupWebSocketsServicios,
    } = useContabilidadModel();

    // Obtener semestres únicos
    const semestresDisponibles = [...new Set(
        solicitudesPorCliente.map(s => s.proPlataforma?.semestre || 'NA')
    )];

    //actualizar semestres
    useEffect(() => {
        if (semestresDisponibles.length > 0 && !semestreSeleccionado) {
          setSemestreSeleccionado(semestresDisponibles[0]);
        }
      }, [semestresDisponibles, semestreSeleccionado]);

    //cargar cliente
    useEffect(() => {
        if (!nombreEmpresaReal || !numeroCliente) return;
        
        let socketCleanup = null;
        
        const cargarCliente = async () => {
            try {
                // Cargar el cliente desde la API
                const clienteObjeto = await getCliente(nombreEmpresaReal, numeroCliente);
                
                // Si se cargó correctamente, configurar el socket
                if (clienteObjeto) {
                    socketCleanup = setupWebSocketsClientes(clienteObjeto, nombreEmpresaReal);
                }
            } catch (error) {
                console.error('Error al cargar cliente:', error);
            }
        };
        
        cargarCliente();
        
        // Cleanup function
        return () => {
            if (socketCleanup) {
                socketCleanup();
            }
        };
        
    }, [nombreEmpresaReal, numeroCliente]);

    //cargar solicitudes
    useEffect(() => {

      
        cargarSolicitudesDeCliente(configuracion.nombreEmpresa, numeroCliente.toString());

        
        const cleanupWebSockets = setupWebSocketsSolicitudes(numeroCliente);

        return () => {
          cleanupWebSockets();
        };
        

    }, [numeroCliente, configuracion.nombreEmpresa]);

    //cargar servicios
    useEffect(() =>{
        console.log("Cargando servicios para el cliente:", numeroCliente);
          getServiciosPorCliente(configuracion.nombreEmpresa, numeroCliente.toString());
          const servicioObj = {};

          
          const cleanupWebSockets = setupWebSocketsServicios(servicioObj,configuracion.nombreEmpresa,numeroCliente);

          
          return () => {
            cleanupWebSockets();
          };
          


    }, [numeroCliente, configuracion.nombreEmpresa]);

    //lista de solicitudes
    const solicitudesFiltradas = useMemo(() => {
        return solicitudesPorCliente
            .filter(s => s.proPlataforma?.noPlataforma) 
            .filter(s => {
                const semestre = s.proPlataforma?.semestre || 'NA';
                return semestre === semestreSeleccionado;
            });
    }, [solicitudesPorCliente, semestreSeleccionado]); 
    
    const serviciosFiltrados = serviciosPorClientes.filter(s => {
        // Condición 1: Asegurarse de que el servicio no sea de plataforma.
        // Esto mantiene el elemento solo si `noPlataforma` es `true`.
        const noEsDePlataforma = s.proPlataforma?.noPlataforma;

        // Condición 2: Asegurarse de que el semestre coincida.
        const semestreServicio = s.proPlataforma?.semestre || 'NA';
        const semestreCoincide = semestreServicio === semestreSeleccionado;

        // El servicio solo se incluirá en el resultado si AMBAS condiciones son verdaderas.
        return noEsDePlataforma && semestreCoincide;
    });

    const volverALista = () => {
        navigate(-1);
    };

    const { resumen: resumenDePagos, planVerificado } = useMemo(() => {
          if (solicitudesFiltradas.length === 0 || serviciosFiltrados.length === 0) {
            return { resumen: [], planVerificado: [] };
          }
        return generarYVerificarPlanCompleto(solicitudesFiltradas, serviciosFiltrados, formatearPrecio);
      
    }, [solicitudesFiltradas, serviciosFiltrados]);

    //VALIDACION DE ERRORES
    const [pdfValidationErrors, setPdfValidationErrors] = useState([]);
    // --- EFECTO PARA VALIDAR DATOS DEL PDF ---
    useEffect(() => {
        const errors = [];
        solicitudesFiltradas.forEach(solicitud => {
            const costo = solicitud.proPlataforma?.costoEmpresa;
            const precio = solicitud.proPlataforma?.precioEmpresa;

            if (costo === undefined || costo === null || isNaN(parseFloat(costo)) || costo <= 0) {
                errors.push(`Solicitud #${solicitud.id} no tiene un 'Costo Tutor' válido.`);
            }
            if (precio === undefined || precio === null || isNaN(parseFloat(precio)) || precio <= 0) {
                errors.push(`Solicitud #${solicitud.id} no tiene un 'Precio Empresa' válido.`);
            }
        });
        setPdfValidationErrors(errors);
    }, [solicitudesFiltradas]);
    const isPdfButtonDisabled = pdfValidationErrors.length > 0;

    if(loadingClientes){
        return (
            <div className="flex items-center justify-center h-64">
            <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
                <div className="mt-2 text-gray-600">.</div>
            </div>
            </div>
        );
    }

    if (loadingSolicitudes) {
        return (
            <div className="flex items-center justify-center h-64">
            <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
                <p className="mt-2 text-gray-600">Cargando informacion de solicitudes...</p>
            </div>
            </div>
        );
    }

    if(loadingServicios){
        return (
            <div className="flex items-center justify-center h-64">
            <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
                <p className="mt-2 text-gray-600">Cargando informacion de los servicios...</p>
            </div>
            </div>
        );
    }

    return (
      <div className="p-6 max-w-7xl mx-auto">
        
        <div className="flex items-center mb-6">
          <button 
            onClick={volverALista}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            <span className="text-xl">←</span>
            <span>Volver a lista</span>
          </button>
        </div>

        {/* Header con botón de regreso */}
        <HeaderProPlataforma
          cliente={cliente}
          nombreEmpresaReal={nombreEmpresaReal}
        />

        {/* Tabs de Semestres */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {semestresDisponibles.map((semestre) => (
                <button
                  key={semestre}
                  onClick={() => setSemestreSeleccionado(semestre)}
                  className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                    semestreSeleccionado === semestre
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  📅 {semestre}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tabs de Secciones */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              <button
                onClick={() => setSeccionActiva('solicitudes')}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  seccionActiva === 'solicitudes'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📋 Solicitudes ({solicitudesFiltradas.length})
              </button>
              <button
                onClick={() => setSeccionActiva('servicios')}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  seccionActiva === 'servicios'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                ⚙️ Servicios Agendados ({serviciosFiltrados.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Contenido según la sección activa */}
        {seccionActiva === 'solicitudes' && (
          <SolicitudesProPlataforma
            solicitudesFiltradas={solicitudesFiltradas}
            semestreSeleccionado={semestreSeleccionado}
            isPdfButtonDisabled={isPdfButtonDisabled}
            pdfValidationErrors={pdfValidationErrors}
            configuracion={configuracion}
            nombreEmpresaReal={nombreEmpresaReal}
            cliente={cliente}
          />
        )}

        {/* Sección de Servicios Agendados */}
        {seccionActiva === 'servicios' && (
          <ServiciosProPlataforma
            serviciosFiltrados={serviciosFiltrados}
            nombreEmpresaReal={nombreEmpresaReal}
            solicitudesFiltradas={solicitudesFiltradas}
          />
        )}

        {/* Fechas de pagos */}
        {resumenDePagos.length > 0 && (
          <div className="mt-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Resumen de Pagos Programados</h3>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pago</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Concepto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Límite de Pago</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Monto a Pagar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {resumenDePagos.map((pago, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{pago[0]}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{pago[1]}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                            {pago[2]}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-800">{pago[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Plan de pagos */}
        {planVerificado.length > 0 && (
          <div className="mt-8 space-y-8">
              {planVerificado.map((pago, index) => (
                  <div key={index}>
                      <h3 className="text-2xl font-bold text-gray-800 mb-4">{`Detalles para: ${pago.pago}`}</h3>
                      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                          <div className="overflow-x-auto">
                              <table className="min-w-full">
                                  <thead className="bg-gray-50">
                                      <tr>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID Solicitud</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto Requerido</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto Cubierto</th>
                                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Saldo Pendiente</th>
                                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                                      </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                      {pago.asociaciones.map((asociacion, idx) => (
                                          <tr key={idx} className="hover:bg-gray-50">
                                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                                                {asociacion.codigo ? asociacion.codigo : `#${asociacion.id}`}
                                              </td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{asociacion.concepto}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800 font-semibold">{formatearPrecio(asociacion.monto)}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">{formatearPrecio(asociacion.montoCubierto)}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-semibold">{formatearPrecio(asociacion.saldoPendiente)}</td>
                                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                                  <span className={`px-3 py-1 text-xs font-semibold leading-5 rounded-full bg-${asociacion.estado.color}-100 text-${asociacion.estado.color}-800`}>
                                                      {asociacion.estado.texto}
                                                  </span>
                                              </td>
                                          </tr>
                                      ))}
                                  </tbody>
                              </table>
                          </div>
                      </div>
                  </div>
              ))}
          </div>
        )}

      </div>
    );

}

export default DetalleProPlataforma
